import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Copy, File as FileIcon } from "lucide-react";
import API, { formatApiError } from "@/lib/api";

const MediaLibrary = () => {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => API.get("/admin/media").then((r) => setItems(r.data.media)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await API.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} archivo(s) subido(s)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const copyUrl = (m) => {
    navigator.clipboard.writeText(m.url);
    toast.success("URL copiada");
  };

  const del = async (m) => {
    if (!window.confirm("¿Eliminar este archivo? Dejará de verse donde esté en uso.")) return;
    try {
      await API.delete(`/admin/media/${m.id}`);
      toast.success("Archivo eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="media-library">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#c9a24b] px-6 py-3 text-sm font-semibold text-[#4b3a2f] transition-colors hover:bg-[#b8913b]">
        <Upload size={16} /> {uploading ? "Subiendo…" : "Subir imágenes, videos, audios o documentos"}
        <input type="file" multiple className="hidden" onChange={upload} data-testid="media-upload-input" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt" />
      </label>

      {items.length === 0 ? (
        <p className="mt-10 rounded-2xl border border-dashed border-[#a47b52]/40 p-10 text-center text-sm text-[#6b5b52]">
          Aún no has subido archivos. Sube una imagen y cópiala en cualquier campo de imagen del sitio.
        </p>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-4 lg:grid-cols-5">
          {items.map((m) => (
            <div key={m.id} className="overflow-hidden rounded-2xl border border-[#e8ded4] bg-white" data-testid={`media-item-${m.id}`}>
              <div className="flex h-28 items-center justify-center bg-[#f2e9dd]">
                {m.is_image ? (
                  <img src={m.url} alt={m.filename} className="h-full w-full object-cover" />
                ) : (
                  <FileIcon size={32} className="text-[#a47b52]" />
                )}
              </div>
              <div className="p-3">
                <p className="truncate text-xs font-medium text-[#4b3a2f]" title={m.filename}>{m.filename}</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-wider text-[#a47b52]">{Math.round(m.size / 1024)} KB</p>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => copyUrl(m)} data-testid={`media-copy-${m.id}`} className="flex h-8 flex-1 items-center justify-center gap-1 rounded-full border border-[#4b3a2f]/15 text-xs text-[#6b5b52] hover:bg-[#f2e9dd]">
                    <Copy size={12} /> Copiar URL
                  </button>
                  <button onClick={() => del(m)} data-testid={`media-delete-${m.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-[#c85a48]/30 text-[#c85a48] hover:bg-[#c85a48] hover:text-white">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
